import {
  VictoryAxis,
  VictoryChart,
  VictoryArea,
  VictoryVoronoiContainer,
  VictoryScatter
} from "victory"
import useStore from "../../../store"
import { useState, useEffect } from "react"
import formatter from "../../../constants/currencyFormatter"
import { formatDateForGraph } from "../../../utilities/formatDateForApi"
import { colors } from "../../../utilities/colors"

export default function CreditUtilizationGraph() {
  const currentPrincipal = useStore((state) => state.dealer.attributes['total-credit-used'])
  const graphRange = useStore((state) => state.graphRange)
  const stats = useStore((state) => state.dealerStats)
  const isGraphHappy = useStore((state) => state.isGraphHappy)
  const setIsGraphHappy = useStore((state) => state.setIsGraphHappy)

  const [hoveredValue, setHoveredValue] = useState({ principal: currentPrincipal, stat_date: formatDateForGraph(new Date()) })

  const today = new Date()

  var startDate = new Date()
  var xAxisTickCount = 12

  switch (graphRange) {
    case '1M':
      startDate.setMonth(today.getMonth() - 1)
      xAxisTickCount = 2
      break
    case '3M':
      startDate.setMonth(today.getMonth() - 3)
      xAxisTickCount = 3
      break
    case '1Y':
      startDate.setYear(today.getYear() - 1)
      break
  }

  // Filters dealer stats by selected date range
  const filteredStats = stats.filter((stat) => {
    return stat.stat_date >= startDate.toISOString().split('T')[0]
  })

  // Adds current principal amount to graph data set
  const data = [...filteredStats, { principal: currentPrincipal, stat_date: formatDateForGraph(today) }]

  // Calculates min and max data points to set graph domain
  const principalArray = data.map((d) => d.principal)
  const min = Math.min(...principalArray)
  const max = Math.max(...principalArray)

  // Calculates average of data set
  const average = principalArray.reduce((a, b) => a + b) / principalArray.length

  // Sets graph color styles
  useEffect(() => {
    if (filteredStats.length > 0 && currentPrincipal >= filteredStats.slice(-1)[0].principal) {
      setIsGraphHappy(true)
    } else {
      setIsGraphHappy(false)
    }
  }, [])

  const resetHoveredValue = () => setHoveredValue({ principal: currentPrincipal, stat_date: formatDateForGraph(new Date()) })

  return (
    <div
      onMouseLeave={resetHoveredValue}
      onTouchEnd={resetHoveredValue}
    >
      <div className="w-full flex flex-col items-end px-3">
        <span className="font-black text-xl" >{hoveredValue ? formatter.format(hoveredValue.principal) : ""}</span>
        <span className="text-md text-grey-300" >{hoveredValue ? new Date(hoveredValue.stat_date).toLocaleDateString([], { month: 'short', day: 'numeric' }) : ""}</span>
      </div>

      <VictoryChart
        domain={{y: [min, max]}}
        domainPadding={{ x: [10, 10], y: [0, 25] }}
        height={650}
        width={900}
        containerComponent={
          <VictoryVoronoiContainer
            voronoiDimension="x"
            onActivated={(points) => {
              if (points.length > 0) {
                setHoveredValue({ principal: points[0].principal, stat_date: points[0].stat_date})
              } else {
                setHoveredValue(null)
              }
            }}
          />
        }
      >
        <VictoryAxis
          tickFormat={(x) => new Date(x).toLocaleDateString([], { month: 'short' })}
          tickCount={xAxisTickCount}
          style={{
            tickLabels: { fill: "transparent", fontFamily: 'roboto' },
            axis: { stroke: "transparent", strokeWidth: 2 },
            ticks: { stroke: "transparent" }
          }}
        />
        <VictoryAxis
          dependentAxis
          orientation="right"
          tickFormat={(x) => `${x/1000}k`}
          tickValues={[average]}
          style={{
            tickLabels: { fill: "transparent", fontFamily: 'roboto' },
            axis: { stroke: "transparent" },
            grid: { stroke: colors.grey, strokeWidth: 2, strokeDasharray: 8 },
            ticks: { stroke: "transparent" }
          }}
        />
        <VictoryArea
          data={data}
          style={{
            labels: {
              fill: colors.grey300,
              fontFamily: 'roboto',
              fontSize: '22px'
            },
            data: {
              stroke: isGraphHappy ? colors.green : colors.fpxRed,
              fill: "transparent",
              strokeWidth: 3
            }
          }}
          labels={({ datum }) => datum.y}
          x={"stat_date"}
          y={"principal"}
          height={250}
        />
        <VictoryScatter
          data={[hoveredValue]}
          x="stat_date"
          y="principal"
          size={6}
          style={{ data: { fill: colors.grey900, stroke: colors.grey100, strokeWidth: 3 }}}
        />
      </VictoryChart>
    </div>
  )
}